/**
 * «Currency first» bank/cash top-up (docs/TARIFF_RATE_MARKUP.md, owner 03.11.2026): «Выбор валюты — наверх. Клиент
 * выбирает валюту (например EUR) и вводит сумму в ней (3000 EUR)» — and «3000 евро — это сумма, которую клиент хочет на
 * счёт, то есть все комсы сверху». The card (USD) is credited the market value of the sum; the client pays the rest on
 * top, at Transfer Hub's rate.
 *
 * Before it the bank/cash form under Transfer Hub funding (spec 07 В1) asked for the credit in the CARD currency even
 * when the client had picked EUR — a client paying in euros typed dollars. The rules of the form live here, pure, so the
 * view and its test read one source.
 */
import type { SourceKind, SubmitBody, TopUpConversion, TopUpEstimate } from '@/api/topup';
import { creditCurrencyFor, type TopUpOptions } from '@/api/topupOptions';

type Opts = Pick<TopUpOptions, 'fiatCredit'> | null | undefined;

/** The form asks «how much on the account, in which currency» — bank/cash while Transfer Hub funds them (fiatCredit). */
export function isCurrencyFirst(kind: SourceKind, opts: Opts): boolean {
  return creditCurrencyFor(kind, opts) != null;
}

/** The currency the amount field is typed in: the one the client picked — on every method now. */
export function amountFieldCurrency(_kind: SourceKind, _opts: Opts, chosen: string): string {
  return (chosen ?? '').toUpperCase();
}

/** The `sourceKind` the estimate is asked with: set exactly when the answer must be «what this sum on the account costs». */
export function estimateSourceKind(kind: SourceKind, opts: Opts): SourceKind | null {
  return isCurrencyFirst(kind, opts) ? kind : null;
}

/** The amounts of the submit: the typed sum is what the client wants on the account; the core derives the rest. */
export function submitAmounts(kind: SourceKind, chosen: string, amountRaw: string):
  Pick<SubmitBody, 'amount' | 'creditAmount' | 'amountCurrency'> {
  void kind;
  return { amount: amountRaw, creditAmount: null, amountCurrency: (chosen ?? '').toUpperCase() };
}

/**
 * The conversion lines of a currency-first estimate, or null for any other estimate:
 * «На счёт: 3 000,00 EUR (≈ 3 240,00 USD)» · rate · commission · «К оплате: 3 152,43 EUR». `rate`, `card`, `pay` are null
 * while the pair's rate is unresolved — the sums then come with the payment details.
 */
export interface ConversionLines {
  account: { amount: string; currency: string };
  /** The account sum on the card, at the market rate. */
  card: { amount: string; currency: string } | null;
  /** The final rate only — owner, 03.11.2026: «Только итоговый курс» (no market, no markup on any client screen). */
  rate: { client: string; from: string; to: string } | null;
  fund: { amount: string; asset: string } | null;
  pay: { amount: string; currency: string } | null;
  fxUnresolved: boolean;
}

export function conversionLines(est: TopUpEstimate | null | undefined): ConversionLines | null {
  const c: TopUpConversion | null | undefined = est?.conversion;
  if (!est || !c) return null;
  return {
    account: { amount: c.accountAmount, currency: c.sendCurrency },
    card: est.grossAmount != null && !est.feePending ? { amount: est.grossAmount, currency: est.currency } : null,
    rate: c.clientRate != null ? { client: c.clientRate, from: c.sendCurrency, to: est.currency } : null,
    fund: c.fund != null ? { amount: c.fund, asset: c.fundAsset } : null,
    pay: c.payAmount != null ? { amount: c.payAmount, currency: c.sendCurrency } : null,
    fxUnresolved: c.fxUnresolved,
  };
}

const intlLocale = (locale: string) => (locale === 'zh' ? 'zh-CN' : locale === 'en' ? 'en-US' : 'ru-RU');

/** A rate on screen: up to 4 decimals, the cabinet language's decimal sign. */
export function formatRate(v: string | number, locale: string): string {
  return Number(v).toLocaleString(intlLocale(locale), { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

/** A percent on screen: «3», «2,5» — no trailing zeros. */
export function formatPercent(v: string | number, locale: string): string {
  return Number(v).toLocaleString(intlLocale(locale), { maximumFractionDigits: 2 });
}
